import React, { useLayoutEffect, lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import { CaseStudyHeader } from '../components/CaseStudyHeader';
import { Footer } from '../components/Footer';
import { CustomCursor } from '../components/CustomCursor';
import { CaseStudyMobileHeader } from '../components/CaseStudyMobileHeader';
import { FloatingButtons } from '../components/FloatingButtons';
import { ChatDrawer } from '../components/ChatDrawer';
import { ReadingProgress } from '../components/ReadingProgress';
import { MobileBottomCTA } from '../components/MobileBottomCTA';
import { CaseStudyNavigation } from '../components/CaseStudyNavigation';
import { usePortfolio } from '../context/PortfolioContext';
import { useCaseStudyNavigation } from '../hooks/useCaseStudyNavigation';
import { useScrollAnimation } from '../utils/AnimationObserver';
import { ShieldCheck, ArrowLeft } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
const fadeUpVariant = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: 'easeOut'
    }
  }
};
function ProofImage({
  src,
  alt,
  caption
}: {
  src: string;
  alt: string;
  caption?: string;
}) {
  return <div className="bg-slate-50 rounded-2xl overflow-hidden border border-slate-200" data-id="element-838">
      <img src={src} alt={alt} loading="lazy" decoding="async" className="w-full h-auto" data-id="element-839" />
      {caption && <p className="text-xs uppercase tracking-[0.2em] text-slate-500 px-6 py-4 text-center bg-slate-50 border-t border-slate-100" style={{
      fontFamily: "'Ronzino', sans-serif"
    }} data-id="element-840">
          {caption}
        </p>}
    </div>;
}
function VimeoEmbed({
  videoId,
  caption,
  aspect = 56.25
}: {
  videoId: string;
  caption?: string;
  aspect?: number;
}) {
  return <div className="bg-white rounded-2xl overflow-hidden border border-slate-200" data-id="element-841">
      <div style={{
      padding: `${aspect}% 0 0 0`,
      position: 'relative'
    }} data-id="element-842">
        <iframe src={`https://player.vimeo.com/video/${videoId}?badge=0&autopause=0&player_id=0&app_id=58479`} frameBorder="0" allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share" referrerPolicy="strict-origin-when-cross-origin" style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%'
      }} title={caption || 'Video'} data-id="element-843" />
      </div>
      {caption && <p className="text-xs uppercase tracking-[0.2em] text-slate-500 px-6 py-4 text-center bg-slate-50 border-t border-slate-100" style={{
      fontFamily: "'Ronzino', sans-serif"
    }} data-id="element-844">
          {caption}
        </p>}
    </div>;
}
const processSteps = [{
  number: '01',
  title: 'Design system architecture',
  desc: 'Led the move from Sketch to Figma. Built a modern design system with reusable components for complex data tables, network topologies, and policy configurations.'
}, {
  number: '02',
  title: 'Threat visualization',
  desc: 'Rebuilt the dashboard to surface critical security events on arrival. Anomalies highlighted, one-click mitigation, no more digging through logs.'
}, {
  number: '03',
  title: 'Policy management',
  desc: 'Designed a visual builder for micro-segmentation. Admins could see the impact of a policy change before it touched production.'
}, {
  number: '04',
  title: 'User research and journey mapping',
  desc: 'Ran 40 interviews across security professional archetypes. Built a service-blueprint hybrid that became the source of truth for design decisions.'
}];
export function CaseStudyCyberSecurity({
  isOverlay = false
}: {
  isOverlay?: boolean;
}) {
  const navigate = useNavigate();
  const {
    viewPreference
  } = usePortfolio();
  useCaseStudyNavigation();
  useScrollAnimation();
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
  }, []);
  return <>
      <ReadingProgress color="#016768" data-id="element-845" />
      {!isOverlay && <CustomCursor data-id="element-846" />}

      <div className="flex min-h-screen bg-white text-slate-900" data-id="element-847">
        <div className="flex-1 min-w-0 flex flex-col relative pb-20 lg:pb-0" data-id="element-848">
          {!isOverlay && <CaseStudyHeader data-id="element-849" />}
          {!isOverlay && <CaseStudyMobileHeader data-id="element-850" />}

          <main className="flex-1 w-full pb-32" data-id="element-851">
            {/* HEADER BLOCK */}
            <motion.section initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-50px'
          }} variants={fadeUpVariant} className="pt-32 sm:pt-40 pb-16 sm:pb-24 px-6 sm:px-12 md:px-16 lg:px-24 max-w-5xl mx-auto w-full" data-id="element-852">
              <button type="button" onClick={() => navigate(viewPreference === 'ai' ? '/portfolio/ai' : '/portfolio/traditional')} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-slate-900 hover:text-slate-500 transition-colors mb-10 group" style={{
              fontFamily: "'Ronzino', sans-serif"
            }} data-id="element-853">
                <ArrowLeft className="w-3.5 h-3.5 transition-transform group-hover:-translate-x-0.5" data-id="element-854" />
                Back to home
              </button>

              <div className="mb-8" data-id="element-855">
                <ShieldCheck className="w-8 h-8 text-[#016768]" weight="light" data-id="element-856" />
              </div>

              <h1 className="text-3xl sm:text-5xl md:text-6xl font-light text-[#016768] leading-[1.1] tracking-tight mb-4" style={{
              fontFamily: "'Gefika', sans-serif"
            }} data-id="element-857">
                Unisys Stealth
              </h1>

              <p className="text-lg sm:text-xl text-slate-500 font-light mb-8" style={{
              fontFamily: "'Gefika', sans-serif"
            }} data-id="element-858">
                Turning a powerful security platform into one teams could
                actually move through.
              </p>

              <p className="text-base sm:text-lg text-slate-700 leading-relaxed max-w-3xl" style={{
              fontFamily: 'system-ui, -apple-system, sans-serif'
            }} data-id="element-859">
                I led the v5 to v6 redesign of Unisys Stealth, an enterprise
                cyber security platform. The legacy version was powerful but
                punishing: engineering-driven UI, technical jargon, and a
                learning curve steep enough to slow critical security decisions.
                v6 was about making it usable on a real shift, without
                sacrificing the depth specialists relied on.
              </p>
            </motion.section>

            {/* HERO IMAGE CARD */}
            <motion.section initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-50px'
          }} variants={fadeUpVariant} className="px-6 sm:px-12 md:px-16 lg:px-24 max-w-5xl mx-auto w-full mb-16 sm:mb-24" data-id="element-860">
              <div className="bg-slate-100 rounded-2xl py-10 sm:py-14 md:py-16 px-6 sm:px-10 md:px-14 flex items-center justify-center" data-id="element-861">
                <img src="https://cdn.magicpatterns.com/uploads/8Z9WqxDbqCWDcRCMWd5zyF/Unisys.png" alt="Unisys Stealth v6 dashboard" className="w-full max-w-none object-contain drop-shadow-xl" data-id="element-862" />
              </div>
            </motion.section>

            {/* META STRIP */}
            <motion.section initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-50px'
          }} variants={fadeUpVariant} className="px-6 sm:px-12 md:px-16 lg:px-24 max-w-5xl mx-auto w-full mb-24 sm:mb-32" data-id="element-863">
              <div className="border-t border-b border-slate-200 py-10" data-id="element-864">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-10" data-id="element-865">
                  <div data-id="element-866">
                    <div className="text-xs uppercase tracking-[0.2em] text-[#016768] mb-2" style={{
                    fontFamily: "'Ronzino', sans-serif"
                  }} data-id="element-867">
                      Product
                    </div>
                    <div className="text-base font-medium text-slate-900" style={{
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                  }} data-id="element-868">
                      Unisys Stealth (v5 to v6)
                    </div>
                  </div>
                  <div data-id="element-869">
                    <div className="text-xs uppercase tracking-[0.2em] text-[#016768] mb-2" style={{
                    fontFamily: "'Ronzino', sans-serif"
                  }} data-id="element-870">
                      My role
                    </div>
                    <div className="text-base font-medium text-slate-900" style={{
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                  }} data-id="element-871">
                      Product Designer
                    </div>
                  </div>
                  <div data-id="element-872">
                    <div className="text-xs uppercase tracking-[0.2em] text-[#016768] mb-2" style={{
                    fontFamily: "'Ronzino', sans-serif"
                  }} data-id="element-873">
                      Timeline
                    </div>
                    <div className="text-base font-medium text-slate-900" style={{
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                  }} data-id="element-874">
                      6 months
                    </div>
                  </div>
                  <div data-id="element-875">
                    <div className="text-xs uppercase tracking-[0.2em] text-[#016768] mb-2" style={{
                    fontFamily: "'Ronzino', sans-serif"
                  }} data-id="element-876">
                      Skills
                    </div>
                    <div className="text-base font-medium text-slate-900" style={{
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                  }} data-id="element-877">
                      Design systems, Research, Enterprise UX, Competitive
                      analysis
                    </div>
                  </div>
                </div>
                <div className="pt-10 border-t border-slate-200" data-id="element-878">
                  <div className="text-xs uppercase tracking-[0.2em] text-[#016768] mb-2" style={{
                  fontFamily: "'Ronzino', sans-serif"
                }} data-id="element-879">
                    Team
                  </div>
                  <div className="text-base font-medium text-slate-900" style={{
                  fontFamily: 'system-ui, -apple-system, sans-serif'
                }} data-id="element-880">
                    Mateo Cohen (Product Designer), Unisys product team, Agency
                    engineering partners
                  </div>
                </div>
              </div>
            </motion.section>

            {/* QUOTE-OVERLAY IMAGE CARD */}
            <motion.section initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-50px'
          }} variants={fadeUpVariant} className="px-6 sm:px-12 md:px-16 lg:px-24 max-w-5xl mx-auto w-full mb-24 sm:mb-32" data-id="element-881">
              <div className="relative rounded-2xl min-h-[280px] sm:h-[480px] sm:aspect-auto overflow-hidden flex items-center justify-center p-6 sm:p-16" data-id="element-882">
                <div className="absolute inset-0 bg-slate-900" aria-hidden="true" data-id="element-883" />

                <div className="relative z-10 text-center max-w-3xl mx-auto" data-id="element-884">
                  <p className="text-lg sm:text-3xl md:text-4xl font-light italic text-white leading-snug" style={{
                  fontFamily: "'Gefika', sans-serif"
                }} data-id="element-885">
                    "This is exactly what we needed to win sales meetings."
                  </p>
                  <div className="text-xs tracking-[0.2em] uppercase text-white/70 mt-6" style={{
                  fontFamily: "'Ronzino', sans-serif"
                }} data-id="element-886">
                    Client business lead
                  </div>
                </div>
              </div>
            </motion.section>

            {/* CONTENT SECTIONS */}
            <section className="px-6 sm:px-12 md:px-16 lg:px-24 max-w-5xl mx-auto w-full space-y-24 sm:space-y-32" data-id="element-887">
              {/* Problem */}
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true,
              margin: '-50px'
            }} variants={fadeUpVariant} data-id="element-888">
                <div className="flex items-center gap-3 mb-4" data-id="element-889">
                  <span className="text-xs tracking-[0.2em] uppercase font-medium" style={{
                  fontFamily: "'Ronzino', sans-serif",
                  color: '#016768'
                }} data-id="element-890">
                    01
                  </span>
                  <span className="h-0.5 w-8 rounded-full" style={{
                  backgroundColor: '#016768'
                }} aria-hidden="true" data-id="element-891" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-medium text-[#016768] mb-6" style={{
                fontFamily: "'Gefika', sans-serif"
              }} data-id="element-892">
                  Problem: Powerful, but punishing
                </h3>
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-6 sm:mb-8" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-893">
                  Stealth v5 was technically deep but built for engineers, not
                  operators. Security professionals worked 8-hour shifts in
                  low-lit rooms, constantly toggling between dark-mode command
                  lines and Stealth's bright, cluttered interface. The visual
                  hierarchy fought them, the jargon slowed them down, and the
                  threat data they needed was buried.
                </p>
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-8" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-894">
                  On the sales side, Illumio was winning meetings on first
                  impression. We needed a redesign that could hold up in a demo
                  and on a 3 a.m. incident response, both.
                </p>

                <ProofImage src="https://cdn.magicpatterns.com/uploads/asQNUakddLpBCPbCr2XyP6/Stealth-image-1.jpg" alt="Unisys Stealth v5 legacy interface" caption="Before: Stealth v5" data-id="element-895" />
              </motion.div>

              {/* Research */}
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true,
              margin: '-50px'
            }} variants={fadeUpVariant} data-id="element-896">
                <div className="flex items-center gap-3 mb-4" data-id="element-897">
                  <span className="text-xs tracking-[0.2em] uppercase font-medium" style={{
                  fontFamily: "'Ronzino', sans-serif",
                  color: '#016768'
                }} data-id="element-898">
                    02
                  </span>
                  <span className="h-0.5 w-8 rounded-full" style={{
                  backgroundColor: '#016768'
                }} aria-hidden="true" data-id="element-899" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-medium text-[#016768] mb-6" style={{
                fontFamily: "'Gefika', sans-serif"
              }} data-id="element-900">
                  Research: 40 interviews across security archetypes
                </h3>
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-901">
                  I ran 40 user interviews across different security
                  professional archetypes, then built a journey map and service
                  blueprint hybrid. That artifact became the source of truth.
                  Every design decision had to map back to a real workflow and a
                  real engineering constraint. When the business pushed for a
                  cleaner-but-shallower direction, the blueprint pushed back.
                </p>
              </motion.div>

              {/* Process Timeline */}
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true,
              margin: '-50px'
            }} variants={fadeUpVariant} data-id="element-902">
                <div className="flex items-center gap-3 mb-4" data-id="element-903">
                  <span className="text-xs tracking-[0.2em] uppercase font-medium" style={{
                  fontFamily: "'Ronzino', sans-serif",
                  color: '#016768'
                }} data-id="element-904">
                    03
                  </span>
                  <span className="h-0.5 w-8 rounded-full" style={{
                  backgroundColor: '#016768'
                }} aria-hidden="true" data-id="element-905" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-medium text-[#016768] mb-6" style={{
                fontFamily: "'Gefika', sans-serif"
              }} data-id="element-906">
                  Process: Four design moves
                </h3>
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-10" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-907">
                  Six months of work, condensed into the moves that did the
                  most.
                </p>

                <div data-id="element-908">
                  {processSteps.map((step, idx) => <div key={step.number} className="relative flex gap-5 sm:gap-8 pb-12 sm:pb-16 last:pb-0" data-id="element-909">
                      {idx !== processSteps.length - 1 && <div className="absolute left-5 sm:left-6 top-10 sm:top-12 bottom-0 w-px bg-slate-200" aria-hidden="true" data-id="element-910" />}
                      <div className="relative z-10 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white border border-slate-300 flex items-center justify-center flex-shrink-0" data-id="element-911">
                        <span className="text-xs sm:text-sm font-medium text-[#016768]" style={{
                      fontFamily: "'Ronzino', sans-serif"
                    }} data-id="element-912">
                          {step.number}
                        </span>
                      </div>
                      <div className="flex-1 pt-1.5 sm:pt-2.5" data-id="element-913">
                        <h4 className="text-lg sm:text-xl font-medium text-slate-900 mb-2" style={{
                      fontFamily: "'Gefika', sans-serif"
                    }} data-id="element-914">
                          {step.title}
                        </h4>
                        <p className="text-base text-slate-600 leading-relaxed max-w-2xl" style={{
                      fontFamily: 'system-ui, -apple-system, sans-serif'
                    }} data-id="element-915">
                          {step.desc}
                        </p>
                      </div>
                    </div>)}
                </div>
              </motion.div>

              {/* Outcome */}
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true,
              margin: '-50px'
            }} variants={fadeUpVariant} data-id="element-916">
                <div className="flex items-center gap-3 mb-4" data-id="element-917">
                  <span className="text-xs tracking-[0.2em] uppercase font-medium" style={{
                  fontFamily: "'Ronzino', sans-serif",
                  color: '#016768'
                }} data-id="element-918">
                    04
                  </span>
                  <span className="h-0.5 w-8 rounded-full" style={{
                  backgroundColor: '#016768'
                }} aria-hidden="true" data-id="element-919" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-medium text-[#016768] mb-6" style={{
                fontFamily: "'Gefika', sans-serif"
              }} data-id="element-920">
                  Outcome: v6 shipped
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 py-8 mb-8 border-y border-slate-100" data-id="element-921">
                  <div data-id="element-922">
                    <div className="text-lg font-medium text-slate-900 mb-2" style={{
                    fontFamily: "'Gefika', sans-serif"
                  }} data-id="element-923">
                      Competitive positioning
                    </div>
                    <div className="text-sm text-slate-600 leading-relaxed" style={{
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                  }} data-id="element-924">
                      Redesign positioned Unisys competitively against Illumio
                      with simplified workflows that kept technical depth
                      intact.
                    </div>
                  </div>
                  <div data-id="element-925">
                    <div className="text-lg font-medium text-slate-900 mb-2" style={{
                    fontFamily: "'Gefika', sans-serif"
                  }} data-id="element-926">
                      Strategic validation
                    </div>
                    <div className="text-sm text-slate-600 leading-relaxed" style={{
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                  }} data-id="element-927">
                      The CrowdStrike partnership I proposed during the design
                      phase was later implemented by the client.
                    </div>
                  </div>
                  <div data-id="element-928">
                    <div className="text-lg font-medium text-slate-900 mb-2" style={{
                    fontFamily: "'Gefika', sans-serif"
                  }} data-id="element-929">
                      Design system adoption
                    </div>
                    <div className="text-sm text-slate-600 leading-relaxed" style={{
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                  }} data-id="element-930">
                      The full component library was adopted for v6 launch and
                      became the foundation for the next platform release.
                    </div>
                  </div>
                  <div data-id="element-931">
                    <div className="text-lg font-medium text-slate-900 mb-2" style={{
                    fontFamily: "'Gefika', sans-serif"
                  }} data-id="element-932">
                      Sales impact
                    </div>
                    <div className="text-sm text-slate-600 leading-relaxed" style={{
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                  }} data-id="element-933">
                      The redesign became the lead artifact in client demos,
                      shifting first impressions of the product on every call.
                    </div>
                  </div>
                </div>

                {/* What shipped — walkthrough video */}
                <div className="mb-10" data-id="element-934">
                  <VimeoEmbed videoId="476985592" caption="Walkthrough: Stealth v6, end to end" data-id="element-935" />
                </div>

                <p className="text-base sm:text-lg text-slate-700 leading-relaxed" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-936">
                  v6 modernized the platform, cut the learning curve for new
                  admins, and gave the sales team a product they could lead
                  with.
                </p>
              </motion.div>
            </section>

            <CaseStudyNavigation previousCase={{
            category: 'Financial Services',
            title: 'US Bank Commercial Card',
            slug: 'us-bank'
          }} nextCase={{
            category: 'AI Comparison',
            title: 'Watson vs Dialogflow',
            slug: 'ai-comparison'
          }} data-id="element-937" />
          </main>
        </div>
      </div>

      <FloatingButtons data-id="element-938" />
      <ChatDrawer data-id="element-939" />
      <MobileBottomCTA data-id="element-940" />
      {!isOverlay && <Footer data-id="element-941" />}
    </>;
}